import React, { HTMLAttributes } from 'react';
import { getIconClassName, mergeStyles } from '@uifabric/styling/lib';
import { isNullOrUndefined } from 'mini-common-utils/lib/utils/isNullOrUndefined';
import { MiniIcons } from 'owa-mini-icons';

const styles = require('./EmbeddedMessage.scss');
const classNames = require('classnames/bind').bind(styles);

type EmbeddedMessageComponentType = 'Icon' | 'Title' | 'Subtitle' | 'CloseButton' | 'CoverImage';

export interface AnchorProps {
    type: 'top' | 'bottom';
    leftInPercent: number;
}

interface EmbeddedMessageComponentProps {
    type?: EmbeddedMessageComponentType;
}

interface CloseButtonProps extends EmbeddedMessageComponentProps {
    onClick: (event: React.SyntheticEvent<EventTarget>) => void;
    ariaLabel?: string;
}

export type IconDataType = 'Svg' | 'MiniIcon' | 'IconStyles';

interface IconProps extends EmbeddedMessageComponentProps {
    iconDataType?: IconDataType;
    iconData?: string;
    customClassName?: string;
}

interface CoverImageProps extends EmbeddedMessageComponentProps {
    imageURL?: string;
}

interface TextComponentProps extends EmbeddedMessageComponentProps {
    customClassName?: string;
}

interface EmbeddedMessageProps {
    anchor?: AnchorProps;
    textIconContainerCustomClassName?: string;
    textContainerCustomClassName?: string;
    closeButtonContainerCustomClassName?: string;
    textAndCoverImageContainerClassName?: string;
    withoutBubble?: boolean;
    onClick?: (event: React.SyntheticEvent<EventTarget>) => void;
    messageWrapperElementProps?: HTMLAttributes<HTMLDivElement>;
}

interface EmbeddedMessageChildren<P> extends React.FC<P> {
    CoverImage?: React.FC<CoverImageProps>;
    Icon?: React.FC<IconProps>;
    Title?: React.FC<TextComponentProps>;
    Subtitle?: React.FC<TextComponentProps>;
    CloseButton?: React.FC<CloseButtonProps>;
}

/**
 * The Cover Image slot
 * @param props
 */
const CoverImage: React.FC<CoverImageProps> = props => {
    const { imageURL } = props;
    return <img src={imageURL} alt="" role="presentation" className={styles.coverImage} />;
};

CoverImage.defaultProps = {
    type: 'CoverImage',
};

/**
 * The Icon slot
 * @param props
 */
const Icon: React.FC<IconProps> = props => {
    const { iconData, iconDataType, children, customClassName } = props;
    const iconContainerClassName = classNames(styles.icon, customClassName);

    switch (iconDataType) {
        case 'IconStyles':
            return (
                <div className={iconContainerClassName}>
                    <i className={classNames(iconData)} aria-hidden="true" />
                </div>
            );

        case 'MiniIcon':
            return (
                <div className={iconContainerClassName}>
                    <i className={classNames(getIconClassName(iconData))} aria-hidden="true" />
                </div>
            );

        case 'Svg':
            return (
                <img src={iconData} alt="" role="presentation" className={iconContainerClassName} />
            );

        default:
            return <div className={iconContainerClassName}>{children}</div>;
    }
};

Icon.defaultProps = {
    type: 'Icon',
};

/**
 * The title slot
 * @param props
 */
const Title: React.FC<TextComponentProps> = props => (
    <div className={classNames(styles.title, props.customClassName)}>{props.children}</div>
);
Title.defaultProps = {
    type: 'Title',
};

/**
 * The subtitle slot
 * @param props
 */
const Subtitle: React.FC<TextComponentProps> = props => (
    <div className={classNames(styles.subtitle, props.customClassName)}>{props.children}</div>
);
Subtitle.defaultProps = {
    type: 'Subtitle',
};

/**
 * The Close button slot
 * @param props
 */
const CloseButton: React.FC<CloseButtonProps> = props =>
    props.children ? (
        <div role="button" onClick={props.onClick} tabIndex={0} aria-label={props.ariaLabel}>
            {props.children}
        </div>
    ) : (
        <i
            role="button"
            onClick={props.onClick}
            tabIndex={0}
            aria-label={props.ariaLabel}
            className={classNames(
                'ms-Icon',
                styles.closeButton,
                getIconClassName(MiniIcons.Cancel)
            )}
        />
    );

CloseButton.defaultProps = {
    type: 'CloseButton',
    ariaLabel: '',
};

/**
 * Component which renders the complete embedded message
 * @param props
 */
const EmbeddedMessage: EmbeddedMessageChildren<EmbeddedMessageProps> = props => {
    if (!props.children) {
        return null;
    }

    const messageChildren = {
        CoverImage: undefined,
        Icon: undefined,
        Title: undefined,
        Subtitle: undefined,
        CloseButton: undefined,
    };

    React.Children.forEach(props.children, child => {
        if (child) {
            if (
                React.isValidElement(child) &&
                !!child.props &&
                !isNullOrUndefined(child.props.type)
            ) {
                messageChildren[child.props.type] = child;
            } else {
                throw new Error(
                    'Children of the EmbeddedMessage should have type property with values CoverImage | Icon | Title | Subtitle | CloseButton'
                );
            }
        }
    });

    const { CoverImage, Icon, Title, Subtitle, CloseButton } = messageChildren;

    if (!(CoverImage || Icon || Title || Subtitle || CloseButton)) {
        return null;
    }

    const { onClick } = props;
    const messageContainerProps = onClick ? { onClick: onClick, role: 'group' } : {};

    const messageComponentRenderer = (
        <div {...messageContainerProps} className={props.textAndCoverImageContainerClassName}>
            {CoverImage && <div className={styles.coverImageContainer}>{CoverImage}</div>}
            <div
                className={classNames(
                    styles.textAndIconContainer,
                    props.textIconContainerCustomClassName
                )}>
                {Icon}
                {(Title || Subtitle) && (
                    <div
                        className={classNames(styles.contents, props.textContainerCustomClassName)}
                        {...props.messageWrapperElementProps}>
                        {Title}
                        {Subtitle}
                    </div>
                )}
                {CloseButton && (
                    <div className={props.closeButtonContainerCustomClassName}>{CloseButton}</div>
                )}
            </div>
        </div>
    );

    // If wrapper div with bubble style is not needed
    if (props.withoutBubble) {
        return messageComponentRenderer;
    }

    const getAnchorElementClassName = () => {
        if (!props.anchor) {
            return styles.plainBubble;
        }

        return classNames(
            props.anchor.type === 'top' ? styles.bubbleTop : styles.bubbleBottom,
            mergeStyles({
                selectors: { ':after': { left: `${props.anchor.leftInPercent}%` } },
            })
        );
    };

    return <div className={getAnchorElementClassName()}>{messageComponentRenderer}</div>;
};

EmbeddedMessage.CoverImage = CoverImage;
EmbeddedMessage.Icon = Icon;
EmbeddedMessage.Title = Title;
EmbeddedMessage.Subtitle = Subtitle;
EmbeddedMessage.CloseButton = CloseButton;
export {
    EmbeddedMessage,
    CoverImage,
    Icon,
    Title,
    Subtitle,
    CloseButton,
    CoverImageProps,
    IconProps,
    CloseButtonProps,
    EmbeddedMessageProps,
    EmbeddedMessageComponentProps,
};
